/**
 * Auto-scroll for wellness journey image tracks (vertical columns + horizontal marquee).
 */
class WellnessJourneyTrack extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {'vertical' | 'horizontal'} */
  #axis = 'vertical';

  connectedCallback() {
    const forceAnimation = this.dataset.forceAnimation === 'true';

    if (!forceAnimation && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.#axis = this.dataset.axis === 'horizontal' ? 'horizontal' : 'vertical';

    const track = this.querySelector(
      this.#axis === 'horizontal' ? '.wellness-journey__marquee-track' : '.wellness-journey__column-track'
    );
    const set = this.querySelector(
      this.#axis === 'horizontal' ? '.wellness-journey__marquee-set' : '.wellness-journey__column-set'
    );

    if (!(track instanceof HTMLElement) || !(set instanceof HTMLElement)) return;

    this.#ensureClone(track, set);
    this.#applyAnimation(track, set);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#applyAnimation(track, set);
    });
    this.#resizeObserver.observe(set);

    this.querySelectorAll('img').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      if (image.complete) return;

      image.addEventListener('load', () => this.#applyAnimation(track, set), { once: true });
    });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} set
   */
  #ensureClone(track, set) {
    const hiddenSelector =
      this.#axis === 'horizontal'
        ? '.wellness-journey__marquee-set[aria-hidden="true"]'
        : '.wellness-journey__column-set[aria-hidden="true"]';

    if (track.querySelector(hiddenSelector)) return;

    const clone = /** @type {HTMLElement} */ (set.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
      node.removeAttribute('data-shopify-editor-block');
    });

    track.appendChild(clone);
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} set
   */
  #applyAnimation(track, set) {
    const styles = getComputedStyle(track);
    const trackGap = parseFloat(styles.rowGap || styles.columnGap || styles.gap) || 0;
    const duration = this.dataset.duration || '40';

    if (this.#axis === 'horizontal') {
      const setWidth = set.getBoundingClientRect().width;
      if (setWidth <= 0) return;

      track.style.setProperty('--wellness-scroll-distance', `${setWidth + trackGap}px`);
    } else {
      const setHeight = set.getBoundingClientRect().height;
      if (setHeight <= 0) return;

      track.style.setProperty('--wellness-scroll-distance', `${setHeight + trackGap}px`);
    }

    track.style.setProperty('--wellness-journey-scroll-duration', `${duration}s`);
  }
}

if (!customElements.get('wellness-journey-column')) {
  customElements.define('wellness-journey-column', class extends WellnessJourneyTrack {});
}

if (!customElements.get('wellness-journey-marquee')) {
  customElements.define('wellness-journey-marquee', class extends WellnessJourneyTrack {
    connectedCallback() {
      this.dataset.axis = 'horizontal';
      super.connectedCallback();
    }
  });
}
